/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './CustomizeAccountsView.scss';
import './DefaultViewSettings.scss';

import { omit } from 'lodash';

import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import Store from 'state/Store';

import RadioGroup from 'components/shared/Radio/RadioGroup';
import PreferencesCol from 'components/layouts/Preferences/shared/PreferencesCol';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesSection from 'components/layouts/Preferences/shared/PreferencesSection';
import {
  getDefaultGroupBy,
  getDefaultByGroupOptions,
  getSelectedDefaultViewSettings
} from 'state/preferences/PreferencesSelectors';
import {
  changeSelectedDefaultStartPage,
  changeSelectedDefaultGroupId
} from 'state/preferences/PreferencesActions';
import { defaultStartPageOptions } from 'state/preferences/PreferencesReducer';
import { groupByIdMap, groupByValueWithId } from 'services/Preferences';
import SaveLoadingButtonWithPermissions from 'components/shared/LoadingButton/SaveLoadingButtonWithPermissions';
import { updateWorkspacePreferences } from 'services/Rest';

class DefaultViewSettings extends React.Component {
  handleStartPageRadioChange = (selectedDefaultStartPage) =>
    Store.dispatch(changeSelectedDefaultStartPage(selectedDefaultStartPage));

  handleGroupByRadioChange = (selectedDefaultGroupId) =>
    Store.dispatch(
      changeSelectedDefaultGroupId(groupByValueWithId[selectedDefaultGroupId])
    );

  onSave = () => {
    const { defaultGroupBy, selectedDefaultViewSettings } = this.props;
    const payloadWithoutRedirectFlag = omit(
      defaultGroupBy,
      'hasRedirectedToDefault'
    );
    const { selectedDefaultStartPage, selectedDefaultGroupId } =
      selectedDefaultViewSettings;
    const defaultGroupByPayload = {
      defaultGroupBy: {
        ...payloadWithoutRedirectFlag,
        accountGroupingId: selectedDefaultGroupId,
        preferredStartPage: selectedDefaultStartPage,
        disposition: {
          action: 'update'
        }
      }
    };
    updateWorkspacePreferences(defaultGroupByPayload);
  };

  shouldDisableSave = () => {
    const { defaultGroupBy, selectedDefaultViewSettings } = this.props;
    const { preferredStartPage, accountGroupingId } = defaultGroupBy;
    const { selectedDefaultGroupId, selectedDefaultStartPage } =
      selectedDefaultViewSettings;

    return (
      accountGroupingId === selectedDefaultGroupId &&
      selectedDefaultStartPage === preferredStartPage
    );
  };

  render() {
    const { defaultGroupByOptions, has529Only, selectedDefaultViewSettings } =
      this.props;
    const { selectedDefaultStartPage, selectedDefaultGroupId } =
      selectedDefaultViewSettings;
    const selectedGroupBy = groupByIdMap[selectedDefaultGroupId];
    const disableSave = this.shouldDisableSave();

    const defaultGroupingBy = selectedGroupBy ? selectedGroupBy : 'retirement';
    const defaultStartPage = selectedDefaultStartPage
      ? selectedDefaultStartPage
      : 'dashboard';

    return (
      <PreferencesSection fullBackground={true}>
        <div className="customize-account-default-grouping-section-header">
          <div className="customize-account-main-header">
            Account View Settings
          </div>
          <PreferencesRow className="start-page-row">
            <PreferencesCol col={12}>
              <div className="customize-accounts-section-header-label">
                Default Homepage
              </div>
              <div className="customize-accounts-section-sub-header">
                Select the page you want to see first after log in:
              </div>
            </PreferencesCol>
          </PreferencesRow>
        </div>
        <div className="customize-account-section-body">
          <PreferencesRow>
            <PreferencesCol col={12}>
              <div className="radio-group">
                <RadioGroup
                  name="default-homepage"
                  onChange={this.handleStartPageRadioChange}
                  options={defaultStartPageOptions}
                  selection={defaultStartPage}
                  className="default-option-input default-grouping-radio"
                />
              </div>
            </PreferencesCol>
          </PreferencesRow>
        </div>
        {!has529Only && (
          <Fragment>
            <div className="start-page-border" />
            <div className="customize-account-default-grouping-section-header">
              <PreferencesRow>
                <PreferencesCol col={12}>
                  <div className="customize-accounts-section-header-label">
                    Default Grouping
                  </div>
                  <div className="customize-accounts-section-sub-header">
                    Select the default grouping for your Accounts page:
                  </div>
                </PreferencesCol>
              </PreferencesRow>
            </div>
            <div className="customize-account-section-body">
              <PreferencesRow>
                <PreferencesCol col={12}>
                  <div className="radio-group">
                    <RadioGroup
                      name="default-grouping"
                      onChange={this.handleGroupByRadioChange}
                      options={defaultGroupByOptions}
                      selection={defaultGroupingBy}
                      className="default-option-input default-grouping-radio"
                    />
                  </div>
                </PreferencesCol>
              </PreferencesRow>
            </div>
          </Fragment>
        )}
        <PreferencesRow>
          <PreferencesCol col={12}>
            <SaveLoadingButtonWithPermissions
              className="customize-accounts-save-button"
              id="save-account-view-settings"
              disabled={disableSave}
              onClick={this.onSave}
              requestStatusKey="updateWorkspacePreferences"
              shouldDisable>
              Save
            </SaveLoadingButtonWithPermissions>
          </PreferencesCol>
        </PreferencesRow>
      </PreferencesSection>
    );
  }
}

const mapStateToProps = (state) => ({
  defaultGroupByOptions: getDefaultByGroupOptions(state),
  defaultGroupBy: getDefaultGroupBy(state),
  selectedDefaultViewSettings: getSelectedDefaultViewSettings(state),
  has529Only: state.view.hasCollegeSavingsOnly
});

export default connect(mapStateToProps)(DefaultViewSettings);
